import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * The onboarding hint kit. Guidance that stays visible while the user types,
 * instead of placeholder text that disappears on the first keystroke.
 */

/** Clickable examples that fill the field — shows what a good answer looks like. */
export function ExampleChips({
  examples,
  onPick,
  label = "Try",
  disabled,
  className,
}: {
  examples: string[];
  onPick: (example: string) => void;
  label?: string;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-wrap items-center gap-1.5", className)}>
      <span className="mr-0.5 text-xs text-muted-foreground">{label}</span>
      {examples.map((example) => (
        <button
          key={example}
          type="button"
          onClick={() => onPick(example)}
          disabled={disabled}
          className="rounded-md border border-border bg-background px-2 py-0.5 text-xs text-foreground/80 transition-colors hover:border-primary/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40 disabled:pointer-events-none disabled:opacity-50"
        >
          {example}
        </button>
      ))}
    </div>
  );
}

/** A keyboard shortcut. Pass "mod" for ⌘ on Apple platforms and Ctrl elsewhere. */
export function KeyHint({ keys, className }: { keys: string[]; className?: string }) {
  return (
    <span
      className={cn("hidden items-center gap-1 text-xs text-muted-foreground sm:inline-flex", className)}
      aria-hidden
    >
      {keys.map((key) => (
        <kbd
          key={key}
          className="min-w-[1.25rem] rounded border border-border bg-muted/60 px-1 py-px text-center font-sans text-[11px] leading-4 text-muted-foreground"
        >
          {key === "mod" ? MOD_KEY : key}
        </kbd>
      ))}
    </span>
  );
}

/** One line of guidance with an optional leading icon. */
export function Hint({ icon, children, className }: { icon?: ReactNode; children: ReactNode; className?: string }) {
  return (
    <p className={cn("flex items-start gap-1.5 text-[13px] leading-snug text-muted-foreground", className)}>
      {icon && <span className="mt-px shrink-0 text-muted-foreground/80">{icon}</span>}
      <span>{children}</span>
    </p>
  );
}

/** "⌘" on Apple platforms, "Ctrl" elsewhere. */
const MOD_KEY =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘" : "Ctrl";
